// Per-blueprint access checks against the OpenFGA **management** store.
//
// A blueprint is keyed by its local platform slug: `blueprint:<localSlug>`.
// Viewer/maintainer cascade in FGA (owner → maintainer → viewer, and down from
// any parent), so a single check per relation is enough — the creator, who is
// granted `owner` on create, resolves to both viewer and maintainer.
//
// Global management-store admins are OR'd in via useCapabilities (authorization
// .read/.write = store:access viewer/maintainer), matching the check already in
// BlueprintDrawer. The backends enforce regardless — this only gates the UI.
import { useQuery } from "@tanstack/react-query";
import { useCapabilities } from "~/auth/capabilities";
import { auth } from "~/auth/index";
import { batchCheckManagement } from "./management";

/** Viewer + maintainer access to a single platform's blueprint. */
export function useBlueprintAccess(localSlug: string | undefined) {
  const { can, loaded: capsLoaded } = useCapabilities();
  const sub = auth.keycloak.tokenParsed?.sub;

  const query = useQuery({
    queryKey: ["blueprint-access", localSlug, sub],
    enabled: Boolean(localSlug && sub),
    staleTime: 60_000,
    queryFn: async () => {
      const res = await batchCheckManagement([
        {
          tupleKey: {
            user: `user:${sub}`,
            relation: "viewer",
            object: `blueprint:${localSlug}`,
          },
          correlationId: "viewer",
        },
        {
          tupleKey: {
            user: `user:${sub}`,
            relation: "maintainer",
            object: `blueprint:${localSlug}`,
          },
          correlationId: "maintainer",
        },
      ]);
      return {
        canView: res.result.viewer?.allowed ?? false,
        canEdit: res.result.maintainer?.allowed ?? false,
      };
    },
  });

  return {
    loaded: capsLoaded && query.data !== undefined,
    canView: can("authorization.read") || (query.data?.canView ?? false),
    canEdit: can("authorization.write") || (query.data?.canEdit ?? false),
  };
}
